import Image from "next/image";
import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
const CustomAccordion = dynamic(() => import("./capabilities-mobile"),{ssr: true});
import { parseHtml, toWebp } from "@/utils/common";

interface CapabilitiesProps {
  slug?: any;
  isMobile?: boolean;
  data: {
    id: number;
    title: string;
    Techstack: any[];
  };
}

const Capabilities: React.FC<CapabilitiesProps> = ({ isMobile, data, slug  }) => {
  const [activeTab, setActiveTab] = useState(0);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  const [viewportHeight, setViewportHeight] = useState<number>(600);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [slug]);

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>(
      ".capability-content"
    );
    let maxHeight = 0;

    elements.forEach((el) => {
      maxHeight = Math.max(maxHeight, el.scrollHeight);
    });
    setViewportHeight(maxHeight);

    // watch for changes
    const resizeObserver = new ResizeObserver(() => {
      let newMax = 0;
      elements.forEach((el) => {
        newMax = Math.max(newMax, el.scrollHeight);
      });
      setViewportHeight(newMax);
    });

    elements.forEach((el) => resizeObserver.observe(el));

    return () => {
      elements.forEach((el) => resizeObserver.unobserve(el));
      resizeObserver.disconnect();
    };
  }, [slug]);

  useEffect(() => {}, [viewportHeight]);

  return (
    <div className="relative sec-capabities">
      {/* Sticky Section */}
      <section className="xl:top-[-250px] md:top-[100px] bg-background-white text-primary-text py-[64px] xl:py-25 md:py-[80px] flex items-start">
        <div className="container max-w-[1020px] mx-auto  w-full relative z-10">
          <h2 className="font-bold text-heading-5  text-primary-text xl:mb-[32px] mb-[24px]">
            {data?.title}
          </h2>

          <div>
            {!isMobile && (
              <>
                {/* Grid container with explicit height */}
                <div className="grid grid-cols-12 gap-4 justify-between relative">
                  {/* Left: Navigation List - This creates the scroll height */}
                  <div className="col-span-12 md:col-span-6">
                    <div className="space-y-8">
                      {data?.Techstack?.map((cap: any, index: any) => {
                        const displayIndex = index + 1;

                        return (
                          <motion.div
                            key={cap?.id}
                            className={`border border-[#B0D9F7] 
                                        relative group min-h-[48px] 
                                        flex xl:flex md:inline-flex w-[90%] xl:w-[98%]
                                        items-center text-subtitle-1 
                                        font-bold px-[12px] py-[16px] cursor-pointer
                                        transition-all duration-200
                                        ${
                                          activeTab === index
                                            ? "border-l-[10px] border-l-[#B0D9F7]-500 bg-[#B0D9F7]-50"
                                            : ""
                                        }`}
                            onClick={() => setActiveTab(index)}
                          >
                            <span
                              className={`font-bold group-hover:text-[#040405] ${activeTab === index ? "text-[#040405]" : "text-[#83858C]"}`}
                            >
                              {displayIndex}.
                            </span>
                            <span
                              className={`ml-2 block group-hover:text-[#040405] ${activeTab === index ? "text-[#040405]" : "text-[#83858C]"}`}
                            >
                              {cap?.title}
                            </span>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right: Content Area - Proper sticky implementation */}
                  <div className="col-span-12 md:col-span-6">
                    {/* Sticky container */}

                    <div className="sticky top-[100px] h-fit">
                      <div
                        className={` bg-white overflow-hidden relative`}
                        style={{ height: `${viewportHeight}px` }}
                      >
                        {data?.Techstack?.map((cap: any, index: any) => {
                          return (
                            <>
                              <motion.div
                                key={index}
                                className="group capability-content p-6 absolute top-0 left-0 w-full border border-[#B0D9F7]" // keep all sections stacked
                                initial={{ opacity: 0 }}
                                animate={{
                                  opacity: activeTab === index ? 1 : 0,
                                  pointerEvents:
                                    activeTab === index ? "auto" : "none", // avoids clicks on hidden tabs
                                }}
                                transition={{
                                  duration: 0.5,
                                  ease: "easeInOut",
                                }}
                              >
                                <motion.div
                                  className="mb-6"
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{
                                    opacity: activeTab === index ? 1 : 0,
                                    y: activeTab === index ? 0 : 20,
                                  }}
                                  transition={{ duration: 0.5, delay: 0.1 }}
                                >
                                  <h3
                                    className={`text-xl font-semibold text-gray-900 leading-relaxed`}
                                  >
                                    {parseHtml(cap?.description)}
                                  </h3>
                                </motion.div>

                                <motion.div
                                  className="w-full"
                                  initial={{ opacity: 0, y: 30 }}
                                  animate={{
                                    opacity: activeTab === index ? 1 : 0,
                                    y: activeTab === index ? 0 : 30,
                                  }}
                                  transition={{ duration: 0.5, delay: 0.2 }}
                                >
                                  {cap?.image?.data?.attributes?.url && (
                                    <div className=" relative w-full h-[287px] overflow-hidden">
                                      <Image
                                        src={toWebp(
                                          `${baseUrl}${cap?.image?.data?.attributes?.url}`
                                        )}
                                        fill
                                        quality={100}
                                        className="object-cover "
                                        alt={cap.title || ""}
                                        loading="lazy"
                                      />
                                    </div>
                                  )}
                                </motion.div>
                              </motion.div>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {isMobile && (
              <>
                <CustomAccordion data={data} />
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Capabilities;
